import React, { useEffect, useState } from "react";
import { Save, Plus, X } from "lucide-react";
import PageHeader from "@/components/admin/PageHeader";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";

const EVENT_DEFS = [
    { key: "leads.submit", label: "Leads submitted", description: "Fires when a guest completes the booking, wedding or join form." },
    { key: "tickets.created", label: "Support ticket created", description: "Fires when a guest opens a support ticket." },
    { key: "conversation.ended", label: "Conversation ended", description: "Fires when a guest closes the chat." },
];

export default function SettingsWebhooks() {
    const [data, setData] = useState(null);
    const [saving, setSaving] = useState(false);
    const [newUrl, setNewUrl] = useState("");

    useEffect(() => {
        adminApi.get("/api/admin/settings/webhooks").then((r) => setData(r.data));
    }, []);

    if (!data) return <div className="p-10 text-stone-500">Loading…</div>;

    const toggleEvent = (key) => {
        const events = new Set(data.events || []);
        events.has(key) ? events.delete(key) : events.add(key);
        setData({ ...data, events: Array.from(events) });
    };

    const addEndpoint = () => {
        if (!newUrl.trim()) return;
        setData({ ...data, endpoints: [...(data.endpoints || []), newUrl.trim()] });
        setNewUrl("");
    };

    const removeEndpoint = (idx) => {
        setData({ ...data, endpoints: data.endpoints.filter((_, i) => i !== idx) });
    };

    const save = async () => {
        if (!(data.events || []).length) {
            toast.error("Select at least one event");
            return;
        }
        if (!(data.endpoints || []).length) {
            toast.error("Add at least one endpoint URL");
            return;
        }
        setSaving(true);
        try {
            await adminApi.put("/api/admin/settings/webhooks", data);
            toast.success("Webhook saved");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-10 max-w-4xl">
            <PageHeader eyebrow="Settings" title="Webhooks" description="Notify external services in real time when something happens in Priya." testid="page-settings-webhooks" />

            <div className="bg-white border border-stone-200 rounded-lg p-6 mb-6">
                <h2 className="font-heading text-xl text-orchid-700 mb-1">Events</h2>
                <p className="text-xs text-stone-500 mb-5">Pick which events trigger a webhook delivery. At least one must be selected.</p>

                <div className="space-y-3" data-testid="webhook-events">
                    {EVENT_DEFS.map((e) => {
                        const checked = (data.events || []).includes(e.key);
                        return (
                            <label
                                key={e.key}
                                className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                                    checked ? "border-orchid-700 bg-orchid-700/5" : "border-stone-200 hover:border-stone-300"
                                }`}
                                data-testid={`webhook-event-${e.key}`}
                            >
                                <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleEvent(e.key)}
                                    className="mt-1 accent-orchid-700"
                                />
                                <div>
                                    <div className="text-sm font-medium text-stone-900">{e.label}</div>
                                    <div className="text-xs text-stone-500 mt-0.5 font-mono">{e.key}</div>
                                    <div className="text-xs text-stone-600 mt-1">{e.description}</div>
                                </div>
                            </label>
                        );
                    })}
                </div>
            </div>

            <div className="bg-white border border-stone-200 rounded-lg p-6 mb-6">
                <h2 className="font-heading text-xl text-orchid-700 mb-1">Endpoints</h2>
                <p className="text-xs text-stone-500 mb-5">Events are sent as a <span className="font-mono">POST</span> request to each endpoint.</p>

                <div className="flex items-center gap-2 mb-4">
                    <input
                        value={newUrl}
                        onChange={(e) => setNewUrl(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && addEndpoint()}
                        placeholder="https://mywebsite.com/webhooks"
                        data-testid="webhook-endpoint-input"
                        className="flex-1 px-3 py-2 rounded-md border border-stone-200 text-sm font-mono focus:outline-none focus:border-orchid-700"
                    />
                    <button
                        onClick={addEndpoint}
                        data-testid="webhook-add-endpoint"
                        className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-md border border-stone-200 hover:bg-stone-50"
                    >
                        <Plus size={14} /> Add
                    </button>
                </div>

                {(data.endpoints || []).length > 0 && (
                    <ul className="space-y-2" data-testid="webhook-endpoints-list">
                        {data.endpoints.map((e, i) => (
                            <li key={i} className="flex items-center justify-between p-3 rounded-md bg-stone-50 border border-stone-200">
                                <span className="text-xs font-mono text-stone-700 truncate flex-1">{e}</span>
                                <button
                                    onClick={() => removeEndpoint(i)}
                                    data-testid={`webhook-remove-endpoint-${i}`}
                                    className="p-1 text-stone-400 hover:text-red-700"
                                >
                                    <X size={14} />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            <button
                onClick={save}
                disabled={saving}
                data-testid="webhook-save"
                className="inline-flex items-center gap-2 bg-orchid-700 hover:bg-orchid-600 text-white text-sm font-medium px-5 py-2.5 rounded-md disabled:opacity-60"
            >
                <Save size={14} /> {saving ? "Saving…" : "Create webhook"}
            </button>
        </div>
    );
}
